export interface PageSection {
    heading?: string;
    content: string;
    image?: string;
    imageCaption?: string;
}

export interface ModulePage {
    title: string;
    heroImage?: string;
    sections: PageSection[];
    keyPoints?: string[];
}

export interface ModuleContent {
    id: number;
    title: string;
    description: string;
    pages: ModulePage[];
}

export const moduleData: ModuleContent[] = [
    {
        id: 1,
        title: "Introduction",
        description: "Welcome to your surgical preparation journey",
        pages: [
            {
                title: "Welcome to SurgReady",
                heroImage: "/surgready/hero-image.png",
                sections: [
                    {
                        heading: "Your Journey Begins Here",
                        content: "Welcome to SurgReady, your comprehensive guide to preparing for surgery. This program has been designed by medical professionals to help you achieve the best possible surgical outcomes through evidence-based preparation strategies.\n\nOver the coming weeks, you'll learn how to optimize your health across multiple dimensions - from nutrition and physical fitness to mental wellbeing and medication management."
                    },
                    {
                        heading: "Why Preparation Matters",
                        content: "Research shows that patients who actively prepare for surgery experience:\n\n• Shorter hospital stays\n• Fewer complications\n• Faster recovery times\n• Better pain management\n• Improved overall outcomes\n\nBy taking this proactive approach, you're already taking an important step toward a successful surgery."
                    }
                ],
                keyPoints: [
                    "Preparation significantly improves surgical outcomes",
                    "This program covers all aspects of surgical readiness",
                    "You'll work through 12 comprehensive modules"
                ]
            },
            {
                title: "How This Program Works",
                sections: [
                    {
                        heading: "Your Personalized Path",
                        content: "SurgReady is organized into 12 modules, each focusing on a critical aspect of surgical preparation. You can work through the modules at your own pace, but we recommend completing them in order for the best experience.\n\nEach module includes:\n• Educational content backed by research\n• Practical tips you can implement immediately\n• Key takeaways to remember\n• Progress tracking to monitor your journey"
                    },
                    {
                        heading: "Setting Your Goals",
                        content: "Throughout this program, you'll have the opportunity to set personal goals based on what you learn. Some goals may be recommended by your healthcare provider, while others you can set yourself.\n\nYour goals dashboard will help you track your progress and stay motivated as your surgery date approaches.",
                        image: "/surgready/running-couple.png",
                        imageCaption: "Setting and achieving goals is key to successful preparation"
                    }
                ],
                keyPoints: [
                    "Work through modules at your own pace",
                    "Set personalized goals for your preparation",
                    "Track your progress on your dashboard"
                ]
            },
            {
                title: "Getting Started",
                sections: [
                    {
                        heading: "Your Countdown Has Begun",
                        content: "Now that you've enrolled in SurgReady, you'll see a countdown to your surgery date on your dashboard. This helps you plan your preparation activities and ensures you're making the most of your available time."
                    },
                    {
                        heading: "Next Steps",
                        content: "After completing this introduction, you'll move on to 'Surgery and You' - a module that helps you understand your specific procedure and what to expect. From there, you'll explore nutrition, physical activity, mental wellbeing, and more.\n\nRemember: Every step you take now is an investment in your recovery. Let's begin this journey together!"
                    },
                    {
                        heading: "Support Along the Way",
                        content: "You're not alone in this journey. SurgReady provides:\n\n• Access to your personalized dashboard\n• Goal tracking and reminders\n• Evidence-based information at your fingertips\n• Connection with your healthcare team's recommendations"
                    }
                ],
                keyPoints: [
                    "Your surgery countdown helps you plan",
                    "Complete modules in order for best results",
                    "Every step improves your surgical outcome"
                ]
            }
        ]
    },
    {
        id: 2,
        title: "Surgery and You",
        description: "Understanding your surgical procedure",
        pages: [
            {
                title: "Understanding Your Surgery",
                heroImage: "/surgready/hero-image.png",
                sections: [
                    {
                        heading: "Knowledge is Power",
                        content: "Understanding your surgical procedure is one of the most empowering steps you can take. When you know what to expect, you can prepare more effectively, ask better questions, and feel more confident going into your surgery."
                    },
                    {
                        heading: "Types of Surgery",
                        content: "Surgeries can be categorized in several ways:\n\n• Elective vs. Emergency - Planned surgeries allow more preparation time\n• Inpatient vs. Outpatient - Some surgeries require overnight stays\n• Minimally Invasive vs. Open - Different approaches have different recovery profiles\n\nYour healthcare team will explain which category your surgery falls into and what that means for your preparation and recovery."
                    }
                ],
                keyPoints: [
                    "Understanding your surgery reduces anxiety",
                    "Different surgery types require different preparation",
                    "Your healthcare team is your best resource"
                ]
            },
            {
                title: "Before, During, and After",
                sections: [
                    {
                        heading: "Before Surgery",
                        content: "In the weeks leading up to your surgery, you'll focus on:\n\n• Optimizing your overall health\n• Following any specific instructions from your surgeon\n• Completing pre-operative tests and appointments\n• Preparing your home for recovery\n• Arranging help for after surgery"
                    },
                    {
                        heading: "The Day of Surgery",
                        content: "On your surgery day, you'll typically:\n\n• Arrive at the hospital or surgical center at your scheduled time\n• Complete final check-in procedures\n• Meet with your anesthesiologist\n• Be prepared for surgery by the nursing team\n• Undergo your procedure\n\nFamily members will be kept informed of your progress throughout.",
                        image: "/surgready/optomise_images/health-navigation-new.jpg",
                        imageCaption: "Your surgical team will guide you through every step"
                    },
                    {
                        heading: "After Surgery",
                        content: "Your recovery begins immediately after surgery. Depending on your procedure, you may:\n\n• Spend time in a recovery room\n• Stay overnight or longer in the hospital\n• Go home the same day\n• Begin physical therapy or rehabilitation\n\nThe better prepared you are, the smoother this transition will be."
                    }
                ],
                keyPoints: [
                    "Preparation happens in the weeks before surgery",
                    "Know what to expect on surgery day",
                    "Recovery planning starts before the operation"
                ]
            },
            {
                title: "Your Surgical Team",
                sections: [
                    {
                        heading: "Meet Your Team",
                        content: "Many healthcare professionals work together to ensure your surgery is successful:\n\n• Surgeon - Performs your operation\n• Anesthesiologist - Manages your anesthesia and comfort\n• Surgical Nurses - Assist during and after surgery\n• Recovery Nurses - Monitor you post-operatively\n• Physical Therapists - Help with rehabilitation\n• Your Primary Care Provider - Coordinates your overall care"
                    },
                    {
                        heading: "Questions to Ask",
                        content: "Don't hesitate to ask your surgical team questions such as:\n\n• What exactly will happen during my surgery?\n• How long will the procedure take?\n• What type of anesthesia will be used?\n• What are the potential risks and complications?\n• What will my recovery look like?\n• When can I return to normal activities?\n\nWriting down your questions beforehand helps ensure you don't forget anything important."
                    }
                ],
                keyPoints: [
                    "A team of professionals supports your surgery",
                    "Write down questions before appointments",
                    "Clear communication leads to better outcomes"
                ]
            }
        ]
    },
    {
        id: 3,
        title: "Nutrition",
        description: "Optimizing your diet for surgery",
        pages: [
            {
                title: "Nutrition and Surgery",
                heroImage: "/surgready/optomise_images/nitrition.jpg",
                sections: [
                    {
                        heading: "Why Nutrition Matters",
                        content: "Proper nutrition is one of the most powerful tools you have for improving your surgical outcomes. Good nutrition helps your body:\n\n• Build and repair tissues faster\n• Fight off infections\n• Maintain muscle strength\n• Support wound healing\n• Reduce inflammation"
                    },
                    {
                        heading: "The Impact on Recovery",
                        content: "Studies show that patients with optimal nutritional status before surgery experience:\n\n• Up to 50% fewer complications\n• Shorter hospital stays\n• Faster wound healing\n• Better energy levels during recovery\n• Reduced risk of infection\n\nEven small improvements in your diet can make a significant difference."
                    }
                ],
                keyPoints: [
                    "Nutrition directly impacts surgical outcomes",
                    "Good nutrition reduces complications",
                    "Start optimizing your diet now"
                ]
            },
            {
                title: "Key Nutrients for Surgery",
                sections: [
                    {
                        heading: "Protein Power",
                        content: "Protein is essential for tissue repair and wound healing. Aim for protein at every meal:\n\n• Lean meats, poultry, and fish\n• Eggs and dairy products\n• Beans, lentils, and legumes\n• Nuts and seeds\n• Tofu and tempeh\n\nMost adults need about 1.2-1.5 grams of protein per kilogram of body weight when preparing for surgery.",
                        image: "/surgready/optomise_images/nitrition.jpg",
                        imageCaption: "Include protein-rich foods at every meal"
                    },
                    {
                        heading: "Vitamins and Minerals",
                        content: "Several micronutrients play crucial roles in surgical recovery:\n\n• Vitamin C - Supports wound healing and immune function\n• Vitamin A - Helps rebuild tissues\n• Zinc - Essential for wound healing\n• Iron - Supports blood production\n• Vitamin D - Important for bone health and immunity\n\nColorful fruits and vegetables are excellent sources of these nutrients."
                    },
                    {
                        heading: "Staying Hydrated",
                        content: "Proper hydration is often overlooked but critically important:\n\n• Aim for 8-10 glasses of water daily\n• Limit caffeine and alcohol\n• Monitor urine color - it should be pale yellow\n• Consider electrolyte drinks if exercising\n\nGood hydration supports all bodily functions and helps medications work effectively."
                    }
                ],
                keyPoints: [
                    "Prioritize protein for tissue repair",
                    "Eat colorful fruits and vegetables daily",
                    "Stay well hydrated before surgery"
                ]
            },
            {
                title: "Your Nutrition Action Plan",
                sections: [
                    {
                        heading: "Simple Changes, Big Impact",
                        content: "You don't need to overhaul your entire diet. Start with these simple steps:\n\n1. Add a protein source to every meal\n2. Eat at least 5 servings of fruits and vegetables daily\n3. Choose whole grains over refined grains\n4. Drink water throughout the day\n5. Limit processed foods and added sugars"
                    },
                    {
                        heading: "Foods to Limit or Avoid",
                        content: "Some foods can interfere with surgery or recovery:\n\n• Alcohol - Affects anesthesia and wound healing\n• Excessive salt - Can increase blood pressure\n• Processed foods - Often low in nutrients\n• Sugary drinks - Provide empty calories\n\nYour surgeon may give you specific dietary instructions to follow before surgery."
                    },
                    {
                        heading: "Consider a Nutrition Consult",
                        content: "If you have specific dietary concerns or conditions like diabetes, a consultation with a registered dietitian can provide personalized guidance. Don't hesitate to ask your healthcare team for a referral if needed."
                    }
                ],
                keyPoints: [
                    "Small dietary changes add up",
                    "Avoid alcohol and processed foods",
                    "Seek professional guidance if needed"
                ]
            }
        ]
    },
    {
        id: 4,
        title: "Supplementation",
        description: "Evidence-based supplements for surgery",
        pages: [
            {
                title: "Supplements and Surgery",
                heroImage: "/surgready/optomise_images/supplements.jpg",
                sections: [
                    {
                        heading: "The Role of Supplements",
                        content: "While a balanced diet should be your primary source of nutrients, supplements can help fill nutritional gaps and optimize your body for surgery. However, it's crucial to approach supplementation carefully, as some supplements can interfere with surgery and medications."
                    },
                    {
                        heading: "Important Considerations",
                        content: "Before taking any supplements:\n\n• Always inform your surgeon about all supplements you take\n• Some supplements must be stopped before surgery\n• Quality varies widely between brands\n• More is not always better\n• Timing matters - some need to be stopped 2-3 weeks before surgery"
                    }
                ],
                keyPoints: [
                    "Supplements can support surgical preparation",
                    "Always disclose supplements to your surgeon",
                    "Some must be stopped before surgery"
                ]
            },
            {
                title: "Helpful Supplements",
                sections: [
                    {
                        heading: "Commonly Recommended Supplements",
                        content: "These supplements may support surgical preparation (consult your doctor first):\n\n• Protein supplements - Help meet increased protein needs\n• Vitamin D - Often deficient in many populations\n• Vitamin C - Supports wound healing\n• Iron - If you have iron-deficiency anemia\n• Probiotics - May support gut health and immunity"
                    },
                    {
                        heading: "Supplements to Avoid Before Surgery",
                        content: "Some supplements increase bleeding risk or interact with anesthesia:\n\n• Fish oil/Omega-3s - May increase bleeding\n• Vitamin E - Can thin blood\n• Garlic supplements - Affects clotting\n• Ginkgo biloba - Increases bleeding risk\n• St. John's Wort - Interacts with many medications\n• Ginseng - May affect blood pressure and clotting\n\nTypically stop these 2-3 weeks before surgery.",
                        image: "/surgready/optomise_images/supplements.jpg",
                        imageCaption: "Discuss all supplements with your surgical team"
                    }
                ],
                keyPoints: [
                    "Some supplements support preparation",
                    "Many must be stopped before surgery",
                    "Always get medical guidance"
                ]
            },
            {
                title: "Your Supplement Plan",
                sections: [
                    {
                        heading: "Creating Your List",
                        content: "Make a complete list of everything you take:\n\n• Prescription medications\n• Over-the-counter medications\n• Vitamins and minerals\n• Herbal supplements\n• Protein powders and shakes\n• Any other supplements\n\nBring this list to every pre-surgical appointment."
                    },
                    {
                        heading: "Working With Your Team",
                        content: "Your healthcare team will help you:\n\n• Identify which supplements are safe to continue\n• Determine which need to be stopped\n• Set a timeline for stopping and restarting\n• Recommend any beneficial additions\n• Monitor for deficiencies\n\nDon't make changes to your supplement routine without consulting your surgical team first."
                    }
                ],
                keyPoints: [
                    "Document all supplements you take",
                    "Follow your team's stopping instructions",
                    "Never hide supplement use from doctors"
                ]
            }
        ]
    },
    {
        id: 5,
        title: "Physical Activity",
        description: "Exercise and prehabilitation",
        pages: [
            {
                title: "Exercise Before Surgery",
                heroImage: "/surgready/optomise_images/exercise-rehab.jpg",
                sections: [
                    {
                        heading: "Prehabilitation",
                        content: "Prehabilitation, or 'prehab,' is the practice of improving your physical fitness before surgery. Just as athletes train before a big event, you can train your body for the 'event' of surgery and recovery.\n\nResearch shows that patients who engage in prehab:\n• Recover faster\n• Have fewer complications\n• Return to normal activities sooner\n• Experience less pain"
                    },
                    {
                        heading: "It's Never Too Late to Start",
                        content: "Even if your surgery is just a few weeks away, any increase in physical activity can help. The key is to start where you are and gradually increase your activity level safely."
                    }
                ],
                keyPoints: [
                    "Prehab improves surgical outcomes",
                    "Any increase in fitness helps",
                    "Start where you are"
                ]
            },
            {
                title: "Types of Exercise",
                sections: [
                    {
                        heading: "Cardiovascular Exercise",
                        content: "Activities that get your heart pumping:\n\n• Walking - The most accessible option\n• Swimming - Low impact on joints\n• Cycling - Stationary or outdoor\n• Dancing - Fun and effective\n\nAim for 30 minutes most days, but even 10-minute sessions help.",
                        image: "/surgready/running-couple.png",
                        imageCaption: "Regular walking is excellent preparation for surgery"
                    },
                    {
                        heading: "Strength Training",
                        content: "Building muscle helps with:\n\n• Getting in and out of bed after surgery\n• Using assistive devices like walkers\n• Maintaining independence\n• Faster return to daily activities\n\nSimple exercises like chair squats, wall push-ups, and resistance band work can make a big difference."
                    },
                    {
                        heading: "Flexibility and Breathing",
                        content: "Don't forget these important components:\n\n• Gentle stretching maintains mobility\n• Deep breathing exercises improve lung function\n• Yoga combines flexibility, strength, and relaxation\n• These practices also reduce stress\n\nBreathing exercises are particularly important for preventing post-surgical lung complications."
                    }
                ],
                keyPoints: [
                    "Combine cardio, strength, and flexibility",
                    "Walking is an excellent starting point",
                    "Breathing exercises are essential"
                ]
            },
            {
                title: "Your Exercise Plan",
                sections: [
                    {
                        heading: "Getting Started Safely",
                        content: "Before starting any new exercise program:\n\n• Check with your doctor first\n• Start slowly and progress gradually\n• Listen to your body\n• Stop if you experience pain or dizziness\n• Stay consistent - regular activity is better than occasional intense workouts"
                    },
                    {
                        heading: "Sample Weekly Plan",
                        content: "A simple prehab program might include:\n\nMonday: 20-minute walk + stretching\nTuesday: Light strength training + breathing exercises\nWednesday: 20-minute walk\nThursday: Rest or gentle yoga\nFriday: 20-minute walk + strength training\nWeekend: Active leisure (gardening, swimming, etc.)\n\nAdjust based on your current fitness level and any restrictions from your doctor."
                    }
                ],
                keyPoints: [
                    "Get medical clearance first",
                    "Start slowly and build up",
                    "Consistency is more important than intensity"
                ]
            }
        ]
    },
    {
        id: 6,
        title: "Mind-Body",
        description: "Mental health and stress management",
        pages: [
            {
                title: "Your Mind Matters",
                heroImage: "/surgready/optomise_images/mindbody.jpg",
                sections: [
                    {
                        heading: "The Mind-Body Connection",
                        content: "Your mental state directly affects your physical health and surgical outcomes. Research shows that patients with positive expectations and lower anxiety levels:\n\n• Require less pain medication\n• Have shorter hospital stays\n• Experience faster wound healing\n• Report better satisfaction with outcomes"
                    },
                    {
                        heading: "It's Normal to Feel Anxious",
                        content: "Feeling worried about surgery is completely normal. You might be anxious about:\n\n• The procedure itself\n• Anesthesia\n• Pain and recovery\n• Time away from work or family\n• The outcome\n\nAcknowledging these feelings is the first step toward managing them."
                    }
                ],
                keyPoints: [
                    "Mental health affects surgical outcomes",
                    "Anxiety before surgery is normal",
                    "Managing stress improves recovery"
                ]
            },
            {
                title: "Stress Management Techniques",
                sections: [
                    {
                        heading: "Deep Breathing",
                        content: "One of the simplest and most effective tools:\n\n1. Breathe in slowly through your nose for 4 counts\n2. Hold for 4 counts\n3. Exhale slowly through your mouth for 6 counts\n4. Repeat 5-10 times\n\nPractice this daily, and use it whenever you feel anxious. This technique also helps prevent lung complications after surgery.",
                        image: "/surgready/optomise_images/mindbody.jpg",
                        imageCaption: "Deep breathing is a powerful stress management tool"
                    },
                    {
                        heading: "Visualization",
                        content: "Imagine your successful surgery and recovery:\n\n• Picture yourself walking into the hospital feeling calm\n• Visualize the surgery going smoothly\n• Imagine waking up comfortable in recovery\n• See yourself healing well at home\n• Envision returning to your normal activities\n\nThis technique, used by athletes before competitions, helps program your mind for success."
                    },
                    {
                        heading: "Mindfulness and Meditation",
                        content: "Even a few minutes of mindfulness daily can reduce anxiety:\n\n• Use a meditation app for guided sessions\n• Focus on the present moment\n• Observe thoughts without judgment\n• Practice gratitude\n• Connect with nature\n\nThese practices build resilience and help you stay calm under pressure."
                    }
                ],
                keyPoints: [
                    "Deep breathing can be done anywhere",
                    "Visualization programs your mind for success",
                    "Even brief mindfulness sessions help"
                ]
            },
            {
                title: "Building Your Support System",
                sections: [
                    {
                        heading: "You're Not Alone",
                        content: "Social support is crucial for surgical recovery:\n\n• Share your feelings with trusted friends and family\n• Accept help when it's offered\n• Join a support group if available\n• Consider talking to a counselor if anxiety is severe\n• Keep your healthcare team informed about how you're feeling"
                    },
                    {
                        heading: "Practical Preparation",
                        content: "Reduce stress by preparing in advance:\n\n• Plan meals for your recovery period\n• Arrange help with household tasks\n• Set up a comfortable recovery space at home\n• Prepare entertainment (books, movies, etc.)\n• Handle work and financial matters beforehand\n\nThe more prepared you are logistically, the calmer you'll feel."
                    }
                ],
                keyPoints: [
                    "Accept support from others",
                    "Practical preparation reduces anxiety",
                    "Seek professional help if needed"
                ]
            }
        ]
    },
    {
        id: 7,
        title: "Sleep",
        description: "Optimizing sleep for recovery",
        pages: [
            {
                title: "Sleep and Surgery",
                heroImage: "/surgready/optomise_images/sleep.jpg",
                sections: [
                    {
                        heading: "Why Sleep Matters",
                        content: "Quality sleep is when your body does its most important repair work. During deep sleep:\n\n• Tissues are repaired and regenerated\n• The immune system is strengthened\n• Hormones that promote healing are released\n• Inflammation is reduced\n• Memory and learning are consolidated"
                    },
                    {
                        heading: "Sleep and Surgical Outcomes",
                        content: "Patients who sleep well before surgery:\n\n• Have better immune function\n• Experience less post-operative pain\n• Recover faster\n• Have better mental clarity\n• Are less susceptible to complications\n\nImproving your sleep now will pay dividends during recovery."
                    }
                ],
                keyPoints: [
                    "Sleep is essential for healing",
                    "Good sleep improves immunity",
                    "Quality matters as much as quantity"
                ]
            },
            {
                title: "Improving Sleep Quality",
                sections: [
                    {
                        heading: "Sleep Hygiene Basics",
                        content: "Create conditions for better sleep:\n\n• Keep a consistent sleep schedule\n• Make your bedroom dark, quiet, and cool\n• Reserve the bed for sleep only\n• Avoid screens for 1 hour before bed\n• Limit caffeine, especially after noon\n• Avoid alcohol close to bedtime",
                        image: "/surgready/optomise_images/sleep.jpg",
                        imageCaption: "A cool, dark, quiet room promotes better sleep"
                    },
                    {
                        heading: "Bedtime Routine",
                        content: "A relaxing routine signals your body it's time to sleep:\n\n• Take a warm bath or shower\n• Read a book (not on a screen)\n• Practice gentle stretching\n• Try deep breathing or meditation\n• Listen to calming music\n• Write in a gratitude journal\n\nStart your routine 30-60 minutes before your desired bedtime."
                    }
                ],
                keyPoints: [
                    "Consistency is key for good sleep",
                    "Create a relaxing bedtime routine",
                    "Optimize your sleep environment"
                ]
            },
            {
                title: "Addressing Sleep Problems",
                sections: [
                    {
                        heading: "Common Sleep Issues",
                        content: "If you struggle with sleep, you're not alone. Common issues include:\n\n• Difficulty falling asleep\n• Waking up during the night\n• Waking too early\n• Snoring or sleep apnea\n• Restless legs\n\nThese issues can often be improved with the right strategies."
                    },
                    {
                        heading: "When to Seek Help",
                        content: "Talk to your healthcare provider if:\n\n• Sleep problems persist despite good sleep hygiene\n• You snore loudly or stop breathing during sleep\n• You feel excessively tired during the day\n• Sleep issues are affecting your daily life\n• You need sleep medications to sleep\n\nSleep disorders like sleep apnea can affect surgical safety and should be diagnosed before surgery."
                    }
                ],
                keyPoints: [
                    "Many sleep issues can be improved",
                    "Sleep apnea should be diagnosed before surgery",
                    "Don't hesitate to seek professional help"
                ]
            }
        ]
    },
    {
        id: 8,
        title: "Substances",
        description: "Alcohol, tobacco, and other substances",
        pages: [
            {
                title: "Substances and Surgery",
                heroImage: "/surgready/optomise_images/chronic-disease.jpg",
                sections: [
                    {
                        heading: "Why This Matters",
                        content: "Certain substances can significantly impact your surgical outcomes. Understanding these effects empowers you to make informed decisions that support your health.\n\nThis module covers alcohol, tobacco, and other substances that may affect your surgery. The goal is not to judge, but to provide information that helps you prepare safely."
                    },
                    {
                        heading: "Being Honest With Your Team",
                        content: "It's essential to be completely honest with your surgical team about your substance use:\n\n• They need this information for your safety\n• It affects anesthesia dosing\n• It helps them anticipate potential complications\n• All information is confidential\n• They are there to help, not to judge"
                    }
                ],
                keyPoints: [
                    "Substances affect surgical safety",
                    "Honesty with your team is essential",
                    "This information is confidential"
                ]
            },
            {
                title: "Alcohol and Tobacco",
                sections: [
                    {
                        heading: "Alcohol",
                        content: "Alcohol affects surgery in several ways:\n\n• Interacts with anesthesia and pain medications\n• Increases bleeding risk\n• Impairs wound healing\n• Weakens immune function\n• Can cause withdrawal symptoms during recovery\n\nRecommendation: Stop alcohol at least 2 weeks before surgery. Even reducing consumption helps.",
                        image: "/surgready/optomise_images/chronic-disease.jpg"
                    },
                    {
                        heading: "Tobacco and Nicotine",
                        content: "Smoking is one of the biggest risk factors for surgical complications:\n\n• Reduces oxygen in blood\n• Slows wound healing significantly\n• Increases infection risk\n• Raises risk of pneumonia\n• Can cause blood clots\n\nRecommendation: Stop smoking at least 4-6 weeks before surgery. All forms of nicotine (patches, vaping, chewing tobacco) should ideally be stopped."
                    }
                ],
                keyPoints: [
                    "Stop alcohol 2+ weeks before surgery",
                    "Stop smoking 4-6 weeks before surgery",
                    "Both significantly affect healing"
                ]
            },
            {
                title: "Other Substances and Support",
                sections: [
                    {
                        heading: "Cannabis and Other Substances",
                        content: "Other substances can also affect surgery:\n\n• Cannabis - Affects anesthesia, can irritate airways\n• Opioids - Tolerance affects pain management\n• Recreational drugs - Various effects on heart, breathing\n• Prescription medications - Must be disclosed\n\nTell your anesthesiologist about all substance use. This information is crucial for your safety."
                    },
                    {
                        heading: "Getting Support",
                        content: "If you need help reducing or stopping substance use:\n\n• Talk to your healthcare provider\n• Ask about smoking cessation programs\n• Consider nicotine replacement therapy (with medical guidance)\n• Use support groups or counseling\n• Focus on your goal: a successful surgery and recovery\n\nYour surgical team can often provide resources and support."
                    }
                ],
                keyPoints: [
                    "Disclose all substance use to your team",
                    "Support is available if you need help",
                    "Stopping now improves your outcomes"
                ]
            }
        ]
    },
    {
        id: 9,
        title: "Social Connection",
        description: "Building your support network",
        pages: [
            {
                title: "The Power of Connection",
                sections: [
                    {
                        heading: "Social Support and Health",
                        content: "Humans are social creatures, and our relationships directly impact our health. Research shows that strong social connections:\n\n• Boost immune function\n• Reduce stress and anxiety\n• Speed recovery from illness\n• Decrease hospital readmissions\n• Improve overall outcomes"
                    },
                    {
                        heading: "Your Recovery Network",
                        content: "Before surgery, it's important to identify people who can support you:\n\n• Who will take you to and from surgery?\n• Who can stay with you the first 24-48 hours?\n• Who can help with meals and household tasks?\n• Who can drive you to follow-up appointments?\n• Who can provide emotional support?"
                    }
                ],
                keyPoints: [
                    "Social support improves outcomes",
                    "Identify your support network now",
                    "Accept help when it's offered"
                ]
            },
            {
                title: "Communicating Your Needs",
                sections: [
                    {
                        heading: "Asking for Help",
                        content: "Many people find it difficult to ask for help. Remember:\n\n• Most people genuinely want to help\n• Be specific about what you need\n• It's okay to accept help even if you could manage alone\n• Helping others makes people feel good\n• You would likely help them if roles were reversed"
                    },
                    {
                        heading: "Coordinating Support",
                        content: "Make it easier for others to help:\n\n• Create a meal train or use a coordination app\n• Make a list of specific tasks and when they're needed\n• Designate one person to coordinate communications\n• Share your surgery date and expected recovery timeline\n• Be clear about visiting hours and preferences",
                        image: "/surgready/hero-image.png",
                        imageCaption: "Clear communication makes it easier for others to help"
                    }
                ],
                keyPoints: [
                    "Be specific when asking for help",
                    "Use tools to coordinate support",
                    "Most people want to help"
                ]
            },
            {
                title: "Staying Connected During Recovery",
                sections: [
                    {
                        heading: "Managing Visitors",
                        content: "While connection is important, you'll also need rest:\n\n• It's okay to limit visiting hours\n• Short visits are often better than long ones\n• Designate a point person to update others\n• Use video calls when in-person visits aren't possible\n• Don't feel obligated to entertain guests"
                    },
                    {
                        heading: "Finding Additional Support",
                        content: "Beyond friends and family, consider:\n\n• Online support groups for your type of surgery\n• Patient advocacy organizations\n• Local community resources\n• Faith communities if applicable\n• Social workers at your hospital\n\nYou don't have to go through this alone. There are many resources available to support you."
                    }
                ],
                keyPoints: [
                    "Balance connection with rest",
                    "Use technology to stay connected",
                    "Explore additional support resources"
                ]
            }
        ]
    },
    {
        id: 10,
        title: "Medication Management",
        description: "Managing your medications safely",
        pages: [
            {
                title: "Medications and Surgery",
                heroImage: "/surgready/optomise_images/health-navigation-new.jpg",
                sections: [
                    {
                        heading: "Why This Matters",
                        content: "Proper medication management before surgery is crucial for your safety. Some medications need to be continued, some adjusted, and others stopped temporarily.\n\nOnly your surgical team should make these decisions. Never stop or change medications on your own without consulting them."
                    },
                    {
                        heading: "Creating Your Medication List",
                        content: "Bring a complete list to every pre-surgical appointment:\n\n• Prescription medications (with doses and times)\n• Over-the-counter medications\n• Vitamins and supplements\n• Herbal remedies\n• Any medications you take 'as needed'\n\nInclude the name, dose, and how often you take each one."
                    }
                ],
                keyPoints: [
                    "Never change medications without medical guidance",
                    "Keep a complete, updated medication list",
                    "Include all supplements and OTC medications"
                ]
            },
            {
                title: "Common Medication Considerations",
                sections: [
                    {
                        heading: "Blood Thinners",
                        content: "Blood thinning medications require careful management:\n\n• Warfarin, aspirin, and other blood thinners may need to be stopped\n• Timing depends on the specific medication\n• Your surgeon will provide specific instructions\n• Never stop blood thinners without medical guidance\n• You may need bridging therapy"
                    },
                    {
                        heading: "Diabetes Medications",
                        content: "Diabetes medication management is complex:\n\n• Insulin doses may need adjustment\n• Some oral medications should be stopped before surgery\n• Fasting before surgery affects blood sugar\n• Close monitoring is essential\n• Your team will provide specific instructions",
                        image: "/surgready/optomise_images/health-navigation-new.jpg",
                        imageCaption: "Work closely with your team on medication management"
                    },
                    {
                        heading: "Other Important Medications",
                        content: "Other medications that require discussion:\n\n• Heart and blood pressure medications\n• Steroids and immunosuppressants\n• Psychiatric medications\n• Hormone therapies\n• Pain medications\n\nYour surgical team will review each of your medications individually."
                    }
                ],
                keyPoints: [
                    "Blood thinners require careful management",
                    "Diabetes medications need adjustment",
                    "Discuss all medications with your team"
                ]
            },
            {
                title: "Your Medication Action Plan",
                sections: [
                    {
                        heading: "Before Surgery",
                        content: "Steps to take:\n\n1. Create your complete medication list\n2. Bring it to all pre-surgical appointments\n3. Follow your team's instructions exactly\n4. Ask questions if anything is unclear\n5. Set reminders for when to stop or change medications\n6. Fill any prescriptions you'll need after surgery"
                    },
                    {
                        heading: "Day of Surgery",
                        content: "On your surgery day:\n\n• Follow instructions about which medications to take\n• Take allowed medications with a small sip of water\n• Bring your medication list to the hospital\n• Inform the surgical team of any last-minute changes\n• Ask about resuming medications after surgery"
                    }
                ],
                keyPoints: [
                    "Follow pre-surgery medication instructions exactly",
                    "Bring your medication list to the hospital",
                    "Ask about resuming medications after surgery"
                ]
            }
        ]
    },
    {
        id: 11,
        title: "Chronic Disease Management",
        description: "Optimizing existing health conditions",
        pages: [
            {
                title: "Managing Chronic Conditions",
                heroImage: "/surgready/optomise_images/chronic-disease.jpg",
                sections: [
                    {
                        heading: "Chronic Disease and Surgery",
                        content: "If you have a chronic health condition, optimizing its management before surgery is one of the most important things you can do.\n\nWell-controlled chronic conditions lead to:\n• Fewer surgical complications\n• Shorter hospital stays\n• Faster recovery\n• Better overall outcomes"
                    },
                    {
                        heading: "Common Conditions",
                        content: "Conditions that benefit from optimization:\n\n• Diabetes\n• High blood pressure\n• Heart disease\n• Lung conditions (COPD, asthma)\n• Kidney disease\n• Autoimmune conditions\n• Obesity\n\nEven conditions that feel 'under control' may benefit from pre-surgical review."
                    }
                ],
                keyPoints: [
                    "Optimize chronic conditions before surgery",
                    "Well-controlled conditions improve outcomes",
                    "Pre-surgical review is important even for 'stable' conditions"
                ]
            },
            {
                title: "Specific Condition Management",
                sections: [
                    {
                        heading: "Diabetes",
                        content: "Blood sugar control is critical:\n\n• Target good blood sugar control in the weeks before surgery\n• High blood sugar increases infection risk\n• Work with your diabetes care team\n• You may need medication adjustments\n• Bring your glucose monitor to the hospital",
                        image: "/surgready/optomise_images/chronic-disease.jpg"
                    },
                    {
                        heading: "Heart and Blood Pressure",
                        content: "Cardiovascular optimization includes:\n\n• Keeping blood pressure in target range\n• Managing cholesterol\n• Controlling heart rhythm issues\n• Reporting any new symptoms\n• Completing any recommended cardiac testing"
                    },
                    {
                        heading: "Lung Conditions",
                        content: "Respiratory preparation:\n\n• Use inhalers as prescribed\n• Practice breathing exercises\n• Report any worsening symptoms\n• Stop smoking if you haven't already\n• Consider a pre-surgical respiratory assessment"
                    }
                ],
                keyPoints: [
                    "Blood sugar control reduces infection risk",
                    "Blood pressure should be well-controlled",
                    "Optimize respiratory function before surgery"
                ]
            },
            {
                title: "Working With Your Healthcare Team",
                sections: [
                    {
                        heading: "Coordinated Care",
                        content: "Your surgical preparation may involve multiple providers:\n\n• Your surgeon\n• Your primary care doctor\n• Specialists managing your chronic conditions\n• An anesthesiologist\n\nMake sure all providers know about your surgery and communicate with each other."
                    },
                    {
                        heading: "Pre-Surgical Clearance",
                        content: "You may need medical clearance:\n\n• This ensures your conditions are optimized\n• Additional tests may be ordered\n• Medications may be adjusted\n• Don't view this as a hurdle - it's for your safety\n• Allow enough time for this process"
                    }
                ],
                keyPoints: [
                    "Ensure all your providers communicate",
                    "Complete pre-surgical clearance requirements",
                    "Allow enough time for optimization"
                ]
            }
        ]
    },
    {
        id: 12,
        title: "The Day Before Surgery",
        description: "Final preparations for your surgery",
        pages: [
            {
                title: "Final Preparations",
                heroImage: "/surgready/hero-image.png",
                sections: [
                    {
                        heading: "You're Almost There",
                        content: "Congratulations on completing your preparation journey! The day before surgery is about final preparations and getting your mind and body ready. Most of the hard work is already done - now it's time to rest and prepare."
                    },
                    {
                        heading: "Final Checklist",
                        content: "Confirm these items:\n\n• Know your arrival time and location\n• Understand fasting instructions\n• Know which medications to take or skip\n• Have someone arranged to take you\n• Have someone to stay with you after discharge\n• Pack a bag for the hospital\n• Set up your recovery area at home"
                    }
                ],
                keyPoints: [
                    "Confirm all logistics",
                    "Know your fasting instructions",
                    "Pack your hospital bag"
                ]
            },
            {
                title: "What to Bring",
                sections: [
                    {
                        heading: "Hospital Bag Essentials",
                        content: "Pack the following items:\n\n• Photo ID and insurance cards\n• Completed paperwork\n• Your medication list\n• Glasses or contacts (leave jewelry at home)\n• Comfortable, loose-fitting clothes for going home\n• Non-slip socks or slippers\n• Phone charger\n• A book or tablet for entertainment"
                    },
                    {
                        heading: "What to Leave at Home",
                        content: "Don't bring:\n\n• Jewelry or valuables\n• Large amounts of cash\n• Unnecessary electronics\n• Anything you'd worry about losing\n\nWear comfortable clothes and minimal makeup or lotion on surgery day.",
                        image: "/surgready/optomise_images/health-navigation-new.jpg",
                        imageCaption: "Pack light and leave valuables at home"
                    }
                ],
                keyPoints: [
                    "Bring ID, insurance, and medication list",
                    "Pack comfortable going-home clothes",
                    "Leave valuables at home"
                ]
            },
            {
                title: "The Night Before",
                sections: [
                    {
                        heading: "Evening Routine",
                        content: "The night before surgery:\n\n• Eat a light dinner (following any dietary instructions)\n• Stop eating at the specified time\n• Continue drinking clear fluids until the cut-off time\n• Take only the medications you've been instructed to take\n• Shower or bathe as instructed\n• Get to bed early"
                    },
                    {
                        heading: "Mental Preparation",
                        content: "Calm your mind:\n\n• Practice your deep breathing exercises\n• Visualize a successful surgery and recovery\n• Remind yourself of your preparation efforts\n• Trust in your healthcare team\n• Focus on positive outcomes\n• Remember: you've done everything you can to prepare"
                    },
                    {
                        heading: "You Are SurgReady!",
                        content: "You've completed all 12 modules of SurgReady. You've optimized your nutrition, fitness, mental health, and overall readiness. You've learned about your surgery and built your support network.\n\nYou are as prepared as you can be. Trust in your preparation, trust in your team, and look forward to your recovery. You've got this!"
                    }
                ],
                keyPoints: [
                    "Follow all fasting instructions exactly",
                    "Get good sleep the night before",
                    "Trust in your preparation - you are SurgReady!"
                ]
            }
        ]
    }
];
